import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AppConfig } from '@/types';
import { loadConfig } from '@/services/config';

interface ConfigContextValue {
  config: AppConfig | null;
  isLoading: boolean;
  error: string | null;
}

const ConfigContext = createContext<ConfigContextValue | undefined>(undefined);

interface ConfigProviderProps {
  children: ReactNode;
}

export function ConfigProvider({ children }: ConfigProviderProps) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadConfig()
      .then((data) => {
        setConfig(data);
        setError(null);
      })
      .catch((err) => {
        console.error('Error loading config:', err);
        setError(err instanceof Error ? err.message : 'Failed to load configuration');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  return (
    <ConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfigContext(): ConfigContextValue {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfigContext must be used within a ConfigProvider');
  }
  return context;
}
